import styled, {css, keyframes} from "styled-components";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {fontFamilies, radii, topbar} from "theme/tokens";
import {type CollapsibleNavItem, pillHiddenCss} from "nav/navCollapse";

/**
 * Externalized styles for {@link NavBar} (owner externalization mandate, 2026-07-12), pulled out of
 * the component when the pill gained its overflow strategy (ui#451): the component file keeps markup
 * + behavior, every styled-components primitive lives here and reads `theme.tokens` exclusively —
 * never a hardcoded color.
 */

/** Design `@keyframes fadedown` — the topbar's entrance. */
const fadeDown = keyframes`
	from {
		transform: translateY(-14px);
	}
	to {
		transform: none;
	}
`;

/**
 * Design `.topbar` (66px / 30px padding; 56px / 18px on mobile) — absolute + chromeless.
 * `pointer-events: none` on the bar itself so the invisible band never swallows clicks meant for
 * page content underneath; only the brand and the links pill are interactive. The height is the
 * shared `topbar` token so authenticated pages reserve exactly this much clearance (see tokens.ts).
 */
export const Topbar = styled.header`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: ${topbar.height};
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 30px;
	box-sizing: border-box;
	z-index: 30;
	background: transparent;
	pointer-events: none;
	animation: ${fadeDown} 0.6s cubic-bezier(0.2, 0.7, 0.2, 1) backwards;

	@media (max-width: 640px) {
		height: ${topbar.heightMobile};
		padding: 0 18px;
	}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

/** Design `.logo` — borderless button so the whole brand block is one accessible control. */
export const Brand = styled.button`
	pointer-events: auto;
	display: flex;
	align-items: center;
	gap: 10px;
	color: ${({theme}) => theme.tokens.ink};
	font-family: ${fontFamilies.sans};
	font-weight: 700;
	font-size: 20px;
	letter-spacing: -0.03em;
	cursor: pointer;
	background: transparent;
	border: none;
	padding: 0;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	/* Keep the brand a ≥44px tap target on a phone even when the wordmark collapses to the 30px
	   logo mark (the mark stays left-aligned; the hit box just grows around it). */
	@media (max-width: 640px) {
		font-size: 18px;
		min-width: 44px;
		min-height: 44px;
	}
`;

/**
 * The "Transferify" wordmark. Both bars pack their pill tighter on a phone — the logged-in bar's
 * destinations + toggle + avatar (ui#96), and the logged-out icon-only marketing links — so
 * the wordmark collapses to the bare logo mark ≤640px in either state, keeping the row
 * on one line at 375px. Desktop (≥641px) always shows it, so the landing wordmark is unchanged there.
 */
export const Wordmark = styled.span`
	@media (max-width: 640px) {
		display: none;
	}
`;

/** The bar's inset around its items; the item radius below is derived from it, so change both here. */
const NAV_BAR_PADDING = "4px";
/** The radius that keeps an item's corner concentric with the bar's across {@link NAV_BAR_PADDING}. */
const NAV_ITEM_RADIUS = `calc(${radii.control} - ${NAV_BAR_PADDING})`;

/** Design `.navlinks` — the links bar (the only surfaced element of the bar). */
export const NavLinks = styled.nav`
	pointer-events: auto;
	display: flex;
	align-items: center;
	gap: 2px;
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	/*
	 * Rounded rectangle, not a pill (ui#2500, owner 2026-09-16, chosen over 16px and 8px on the
	 * running preview): the bar takes radii.control, the same 12px every button and input in the
	 * app takes, so the chrome above the page belongs to the controls' radius family instead of
	 * being the single fully-rounded thing on screen.
	 *
	 * The radius is FIXED rather than height-derived, which is the substantive difference from the
	 * end-cap rule it replaces. It therefore stays 12px at every size the bar takes — labelled
	 * desktop, icon-only below 640px, either catalog's label widths — and the inner highlights are
	 * what have to be kept concentric with it (see navLinkBase), because they no longer both
	 * collapse to half of their own height.
	 *
	 * Comments inside this template literal carry NO backticks: a backtick here closes the
	 * styled-components string and the file stops parsing.
	 */
	border-radius: ${radii.control};
	padding: ${NAV_BAR_PADDING};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
`;

/** Design `.navlink` — shared by the public buttons and the logged-in destination links. */
const navLinkBase = css`
	color: ${({theme}) => theme.tokens.ink2};
	font-family: ${fontFamilies.sans};
	font-weight: 500;
	font-size: 14px;
	padding: 9px 13px;
	/* CONCENTRIC with the bar (ui#2500): an item's corner sits NAV_BAR_PADDING inside the bar's, and
	   the two curves stay parallel only when the item's radius is the bar's minus that padding. A
	   larger radius crowds the bar's corner and a smaller one leaves a visible wedge of card between
	   the two curves. Computed from the same two values the bar uses, so moving either moves this
	   one with it. Applies at every size, including the ≤640px icon squares. */
	border-radius: ${NAV_ITEM_RADIUS};
	cursor: pointer;
	background: transparent;
	border: none;
	transition: 0.16s;

	&:hover {
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	/*
	 * 641–767px is the tightest LABELLED band: the pill still shows every localized word but has the
	 * least room to show it in, and Topbar is a space-between flex row whose two children both keep the
	 * flex default min-width:auto — so nothing shrinks, and a pill wider than the row simply runs off
	 * the right of the screen rather than compressing.
	 *
	 * Romanian is what makes that reachable ("Funcționalități", "Autentificare"), and ui#1177's wider
	 * text face is what crossed it: measured at 641px the bar went from 465.58px wide with 12.08px of
	 * gutter to 482.84px with -7.98px — eight pixels of navigation off-screen.
	 *
	 * The horizontal padding therefore steps to the MOBILE value here while the vertical rhythm, the
	 * font size and the labels all stay desktop. That is the same footprint-reclaiming move the ≤640px
	 * rules already make (see NavSeparator, dropped to keep the row inside a 375px viewport) — applied
	 * one band earlier because the labels arrive there first.
	 */
	@media (min-width: 641px) and (max-width: 767px) {
		padding: 9px 9px;
	}

	@media (max-width: 640px) {
		padding: 8px 9px;
		font-size: 13px;
	}
`;

export const NavLink = styled.button`
	${navLinkBase};
`;

/**
 * Icon-only mobile treatment (ui#96): collapse a pill item to a fixed 44px square holding just its
 * centred glyph, so text labels never widen the row at 375px. Shared by the logged-in destinations
 * and the logged-out marketing links — the same discipline applied to both bars.
 */
const iconOnlyOnMobile = css`
	@media (max-width: 640px) {
		width: 44px;
		height: 44px;
		padding: 0;
		justify-content: center;
	}
`;

/**
 * Takes a pill item OUT of the bar at the measured widths where the ProfileMenu shows it instead
 * (ui#451). Both halves of the swap are generated from the SAME entry in `NAV_COLLAPSE_RANGE` — this
 * one and `collapsedNavCss` in `ProfileMenu.styles` — so an item can never be hidden in the pill
 * without appearing in the menu at exactly the same widths. Personalization's range is a BAND rather
 * than a ceiling (ui#452), which is why the query composition lives in the rule module and not here.
 */
export const pillOnlyNavCss = (item: CollapsibleNavItem) => css`
	${pillHiddenCss(item)}
`;

/** Crawlable public link whose plain primary activation can open an in-place marketing dialog. */
export const MarketingAnchor = styled(RouterLink)`
	${navLinkBase};
	display: inline-flex;
	align-items: center;
	text-decoration: none;
	${iconOnlyOnMobile};
`;

/**
 * A pill item that is a `<button>` rather than an anchor, with the same icon-only treatment as the
 * anchors. Two users, and since ui#1292 they are buttons for two DIFFERENT reasons: Resources, on both
 * bars, opens a dropdown in place and routes nowhere; Personalization, on the logged-in bar, does now
 * address a route (`/personalization`) but TOGGLES it — one control that opens and closes the surface
 * (ui#1243) — which is a button's job and not a link's.
 *
 * Personalization also passes `$collapse`, and since ui#452 its range is a BAND: it leaves the bar
 * between 641px and 879px and is back in it — as the 44px icon square — on the narrow viewports either
 * side of that.
 */
export const PillButton = styled(NavLink)<{$collapse?: CollapsibleNavItem}>`
	display: inline-flex;
	align-items: center;
	${iconOnlyOnMobile};
	${({$collapse}) => ($collapse ? pillOnlyNavCss($collapse) : null)};
`;

/**
 * Design's ACTIVE pill — the tint + ink pair that marks the one item the bar is currently showing:
 * `accentTint` + blue, or a signed-in Premium account's colour shaded until it reads as text (the
 * active-nav role, kitecorp/transferify-ui#2935).
 *
 * Shared (ui#1243) because there are now two different facts that make an item current: the router's
 * `aria-current="page"` on a destination, and an open sheet's `aria-expanded="true"` on the button
 * that raised it. They are the same state to a user, so they cannot be allowed to become two
 * different-looking "active"s — one definition, keyed twice.
 *
 * It has the SAME specificity as {@link navLinkBase}'s `&:hover` (one class + one simple selector
 * either way), so what decides the tie is source order — and at both use sites this pair is emitted
 * after that hover rule: inside `DestinationLink` it is written below `${navLinkBase}`, and
 * `SheetPillButton` is a derived component, whose block styled-components appends after its base's.
 * An active item therefore keeps its accent while the pointer is over it, exactly as `LoginButton`
 * relies on to override the same hover from its own derived block.
 */
const activePillCss = css`
	background: ${({theme}) => theme.tokens.navActiveTint};
	color: ${({theme}) => theme.tokens.navActiveInk};
`;

/**
 * A {@link PillButton} that OWNS an overlay and therefore has an open state to draw (ui#1243).
 *
 * Personalization is the case, and before ui#1243 the pill looked identical open and closed. Keyed on
 * the `aria-expanded` the button already publishes for assistive tech rather than on a second boolean
 * prop, so the announced state and the drawn state are one fact.
 *
 * It stayed `aria-expanded` when ui#1292 gave the sheet a route of its own, rather than moving to the
 * `aria-current` every route pill beside it is lit by. The control is a TOGGLE: what a screen-reader
 * user needs to hear before pressing it again is whether the surface is open, not whether they are on
 * its page. Since ui#1292 that open state IS the route, so a deep link lights the pill with nothing on
 * the bar having been clicked.
 *
 * Deliberately NOT folded into `PillButton` itself: the Resources trigger is one too and publishes the
 * same attribute while its hover dropdown is open. Lighting a hover menu's trigger the way a selected
 * destination is lit is a design decision about a different control, and this ticket does not make it.
 */
export const SheetPillButton = styled(PillButton)`
	&[aria-expanded="true"] {
		${activePillCss};
	}
`;

/**
 * Design `.login-btn`, weight 600 — OUTLINED, not text or fill (ui#814, owner): a visible border in
 * the brand blue makes the one account action read as a button against the bar's plain links. It
 * restates the item radius rather than inheriting it because its own `border` makes the radius
 * matter in a way the borderless links' does not: this is the one item whose corner is DRAWN, so a
 * mismatch against the bar's corner is visible as a stroked curve rather than only as a fill. Same
 * {@link NAV_ITEM_RADIUS} and the same reason as {@link navLinkBase} — concentric with the bar
 * across its padding (ui#2500). The padding gives back the border's 1px per side, keeping the
 * height of its text-button neighbours.
 */
export const LoginButton = styled(NavLink)`
	color: ${({theme}) => theme.tokens.blue};
	font-weight: 600;
	padding: 8px 15px;
	white-space: nowrap;
	border: 1px solid ${({theme}) => theme.tokens.blue};
	border-radius: ${NAV_ITEM_RADIUS};

	&:hover {
		background: ${({theme}) => theme.tokens.accentTint};
		color: ${({theme}) => theme.tokens.blue};
	}

	/* Takes the mobile horizontal padding in the tight labelled band, for the reason navLinkBase gives. */
	@media (min-width: 641px) and (max-width: 767px) {
		padding: 8px 12px;
	}

	@media (max-width: 640px) {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-height: 44px;
		padding: 0 12px;
	}
	@media (max-width: 360px) {
		width: 44px;
		min-width: 44px;
		padding: 0;
	}

`;

/** At the smallest phone width the same login action uses the existing icon-only pill pattern. */
export const LoginGlyph = styled.span`
	display: none;
	@media (max-width: 360px) { display: inline-flex; }
`;
export const LoginLabel = styled.span`
	@media (max-width: 360px) { display: none; }
`;

/** Design `.nav-sep`. */
export const NavSeparator = styled.span`
	width: 1px;
	align-self: stretch;
	margin: 4px 4px;
	background: ${({theme}) => theme.tokens.cardBorder};
	flex: none;

	/* Drop the thin divider on a phone: it is the least essential pill element, and reclaiming its
	   footprint keeps the row within the viewport at 375px (ui#111, ui#117). */
	@media (max-width: 640px) {
		display: none;
	}
`;

/**
 * Logged-in destination: the `.navlink` idiom as a router link. Current route reads `accentTint` +
 * blue (design's active pill); an inactive destination (account not yet activated, #12) drops to
 * `ink3` and stops taking pointer/keyboard input. Text shows on desktop, icon-only ≤640px, and a
 * `$collapse` destination leaves the bar entirely below its threshold (ui#451).
 */
export const DestinationLink = styled(RouterLink)<{$collapse?: CollapsibleNavItem}>`
	${navLinkBase};
	display: flex;
	align-items: center;
	text-decoration: none;

	&[aria-current="page"] {
		${activePillCss};
	}

	&[aria-disabled="true"] {
		color: ${({theme}) => theme.tokens.ink3};
		cursor: default;
		pointer-events: none;
	}

	${iconOnlyOnMobile};
	${({$collapse}) => ($collapse ? pillOnlyNavCss($collapse) : null)};
`;

/** Keeps the reused theme toggle at a ≥40px tap target ≤640px (its icon button is smaller). */
export const ToggleSlot = styled.span`
	display: inline-flex;

	@media (max-width: 640px) {
		& > button {
			min-width: 40px;
			min-height: 40px;
		}
	}
`;

/** A pill item's text label — shown ≥641px, hidden ≤640px where the pill goes icon-only. Shared by
 *  the logged-in destinations and the logged-out marketing links (#106). */
export const PillLabel = styled.span`
	@media (max-width: 640px) {
		display: none;
	}
`;

/** A pill item's glyph — hidden ≥641px (desktop shows the text label), shown ≤640px. */
export const PillIcon = styled.span`
	display: none;

	@media (max-width: 640px) {
		display: inline-flex;
		align-items: center;
	}
`;
